import React, { useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import crypto from "./Crypto";
import TextField from "@mui/material/TextField";
import { CapSentence } from "./utils/Utils";
import Enumerable from "linq";
import LoadingButton from "@mui/lab/LoadingButton";

export default function CoeAutocomplete({
  selectedClinica,
  selectedEspecialidad,
  selectedOdontologo,
  setSelectedClinica,
  setSelectedEspecialidad,
  setSelectedOdontologo,
  handlebusqueda = null,
  loading = null,
  stClinica = null,
  stEspecialidad = null,
  stOdontologo = null,
  stEspecialidadesLista = null,
  stOdontologosLista = null,
  handleOnChange = null,
  onlyEspecialidad = false,
  flexdirection = "row",
  visibleClinica = true,
  visibleEspecialidad = true,
  visibleOdontologo = true,
  onChangeClinica = null,
  clinicaWidth = "180px",
  especialidadWidth = "250px",
  odontologoWidth = "250px",
  formik = null,
}) {
  const allCentros = crypto.decryptDataStorage("centros") || [];
  const allCoes = crypto.decryptDataStorage("allcoes") || [];

  const cargarEspecialidades = (clinicaId) => {
    return Enumerable.from(allCoes)
      .where((w) => w.centro_id === clinicaId)
      .groupBy((g) => g.especialidad_id)
      .select((e) => ({
        id: e.key(),
        especialidad: e.max((m) => m.especialidad),
      }))
      .toArray();
  };

  const cargarOdontologos = (clinicaId, especialidadId) => {
    return Enumerable.from(allCoes)
      .where((w) => !clinicaId || w.centro_id === clinicaId)
      .where((w) => w.especialidad_id === especialidadId)
      .groupBy((g) => g.odontologo_id)
      .select((e) => ({
        id: e.key(),
        odontologo: e.max((m) => m.odontologo),
      }))
      .toArray();
  };

  const [especialidadesLista, setEspecialidadesLista] = useState(
    crypto.decryptDataStorage(stEspecialidadesLista) || []
  );
  const [odontologosLista, setOdontologosLista] = useState(
    crypto.decryptDataStorage(stOdontologosLista) || []
  );

  const onClinicasChange = (event, value) => {
    handleOnChange?.();
    onChangeClinica?.(value);
    setSelectedClinica(value);
    if (formik) {
      formik.setFieldError("clinica", "");
      formik.setTouched("clinica", true, true);
    }

    if (stClinica) {
      value
        ? crypto.encryptDataStorage(stClinica, value)
        : sessionStorage.removeItem(stClinica);
    }

    if (!value) {
      setSelectedEspecialidad(null);
      setSelectedOdontologo(null);
      if (stEspecialidad) sessionStorage.removeItem(stEspecialidad);
      if (stOdontologo) sessionStorage.removeItem(stOdontologo);
      setEspecialidadesLista([]);
      setOdontologosLista([]);
      return;
    }

    if (visibleEspecialidad) {
      const nuevas = cargarEspecialidades(value.id);
      setSelectedEspecialidad(null);
      setEspecialidadesLista(nuevas);
      if (stEspecialidadesLista)
        crypto.encryptDataStorage(stEspecialidadesLista, nuevas);
    }

    if (visibleOdontologo && !visibleEspecialidad) {
      const nuevosOdontologos = Enumerable.from(allCoes)
        .where((w) => w.centro_id === value.id)
        .groupBy((g) => g.odontologo_id)
        .select((e) => ({
          id: e.key(),
          odontologo: e.max((m) => m.odontologo),
        }))
        .toArray();

      setOdontologosLista(nuevosOdontologos);
      setSelectedOdontologo(null);
      if (stOdontologosLista)
        crypto.encryptDataStorage(stOdontologosLista, nuevosOdontologos);
    }
  };

  const onEspecialidadesChange = (event, value) => {
    handleOnChange?.();
    setSelectedEspecialidad(value);
    if (formik) {
      formik.setFieldError("especialidad", "");
      formik.setTouched("especialidad", true, true);
    }

    setSelectedOdontologo(null);

    if (stEspecialidad) {
      value
        ? crypto.encryptDataStorage(stEspecialidad, value)
        : sessionStorage.removeItem(stEspecialidad);
    }

    if (!value) {
      setOdontologosLista([]);
      return;
    }

    if (visibleOdontologo) {
      const nuevos = cargarOdontologos(selectedClinica?.id ?? null, value.id);
      setOdontologosLista(nuevos);
      if (stOdontologosLista)
        crypto.encryptDataStorage(stOdontologosLista, nuevos);
    }
  };

  const onOdontologosChange = (event, value) => {
    handleOnChange?.();
    setSelectedOdontologo(value);
    if (formik) {
      formik.setFieldError("odontologo", "");
      formik.setTouched("odontologo", true, true);
    }

    if (stOdontologo) {
      value
        ? crypto.encryptDataStorage(stOdontologo, value)
        : sessionStorage.removeItem(stOdontologo);
    }
  };

  return (
    <Box
      sx={{
        marginTop: -1,
        marginBottom: 2,
        textTransform: "capitalize",
        display: "flex",
        flexDirection: { xs: "column", md: flexdirection },
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {visibleClinica && (
        <Autocomplete
          id="clinica"
          sx={{ width: clinicaWidth, px: 1, py: 1 }}
          options={allCentros}
          value={selectedClinica}
          autoHighlight
          size="small"
          onChange={onClinicasChange}
          getOptionLabel={(option) => option.razonsocial}
          renderOption={(props, option) => (
            <Box
              component="li"
              sx={{ display: "flex", alignItems: "center" }}
              {...props}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: option.color,
                  marginRight: 1,
                  border: 1,
                  borderColor: "black",
                }}
              >
                {option.identificador}
              </Avatar>
              {option.razonsocial}
            </Box>
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Clínica"
              variant="outlined"
              error={
                formik?.touched?.clinica && Boolean(formik?.errors?.clinica)
              }
              helperText={formik?.touched?.clinica && formik?.errors?.clinica}
            />
          )}
        />
      )}

      {visibleEspecialidad && (
        <Autocomplete
          id="especialidad"
          sx={{ width: especialidadWidth, px: 1, py: 1 }}
          options={especialidadesLista}
          value={selectedEspecialidad}
          autoHighlight
          size="small"
          onChange={onEspecialidadesChange}
          getOptionLabel={(option) => option.especialidad}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Especialidad"
              variant="outlined"
              error={
                formik?.touched?.especialidad &&
                Boolean(formik?.errors?.especialidad)
              }
              helperText={
                formik?.touched?.especialidad && formik?.errors?.especialidad
              }
            />
          )}
        />
      )}

      {visibleOdontologo && (
        <Autocomplete
          id="odontologo"
          sx={{ width: odontologoWidth, px: 1, py: 1 }}
          options={odontologosLista}
          value={selectedOdontologo}
          autoHighlight
          size="small"
          onChange={onOdontologosChange}
          getOptionLabel={(option) => CapSentence(option.odontologo)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Odontólogo"
              variant="outlined"
              error={
                formik?.touched?.odontologo &&
                Boolean(formik?.errors?.odontologo)
              }
              helperText={
                formik?.touched?.odontologo && formik?.errors?.odontologo
              }
            />
          )}
        />
      )}

      {handlebusqueda && (
        <LoadingButton
          size="medium"
          onClick={handlebusqueda}
          loading={loading}
          loadingPosition="end"
          endIcon={<SearchIcon />}
          sx={{
            backgroundColor: "primary.main",
            ":hover": { bgcolor: "#475569", color: "white" },
            height: 40,
            marginTop: { xs: 2, md: 0 },
            marginLeft: { xs: -18, md: flexdirection === "row" ? 0 : -10 },
          }}
          variant="contained"
        >
          <span></span>
        </LoadingButton>
      )}
    </Box>
  );
}
